﻿import React, { useMemo } from "react";
import { IndividualUnitDataModel } from "../../interfaces/individualUnitDataModel";
import { LineLevelCouponModel } from "../../interfaces/lineLevelCouponModel";
import {
  LineLevelCouponDiscDisplay,
  PriceIntroduction,
  PurchaseProcess,
} from "../../constants/enums";
import { NavitorPrice } from "../../interfaces/navitorPrice";
import { WBMFormatMoney } from "wbm-format-money";

type ProductPriceProps = {
  unitPrice: number;
  displayUnitPricing: boolean;
  lineLevelCouponSavings: LineLevelCouponModel | null;
  lineLevelCouponDisplayType: LineLevelCouponDiscDisplay;
  individualUnitData?: IndividualUnitDataModel;
  unitId: string;
  salesPrice: number;
  listPrice: number;
  displayListPrice: boolean;
  qtyBreaks: JSX.Element;
  oncePerCustomer: boolean;
  qtyLimit: number;
  couponCustomerLimitMessage: string;
  couponQtyLimitMessage: string;
  displayFootnotes: boolean;
  aboveSalesUnitPrice: boolean;
  belowSalesUnitPrice: boolean;
  navitorPrice?: NavitorPrice;
  purchaseProcess?: number;
};

export const ProductPrice = ({ ...props }: ProductPriceProps) => {
  const { navitorPrice, purchaseProcess } = props;

  const isNavitorProduct = purchaseProcess === PurchaseProcess.NavInk;

  if (!navitorPrice && isNavitorProduct) {
    return <div className="skeleton navitor-price-placeholder"></div>;
  }

  const salesPrice = (
    <WBMFormatMoney amount={props.salesPrice} useCurrencySign />
  );
  const listPrice = (
    <WBMFormatMoney
      amount={props.listPrice}
      useCurrencySign
      className="list-price"
    />
  );
  let unitPriceByIndUnit = 0;
  if (props.individualUnitData) {
    unitPriceByIndUnit =
      props.unitPrice / props.individualUnitData.indivUnitCount;
  }
  const unitPriceByIndUnitFormatted = (
    <WBMFormatMoney
      amount={Number(unitPriceByIndUnit?.toFixed(2))}
      useCurrencySign
    />
  );
  const lineLevelCouponSavings = (
    <WBMFormatMoney
      amount={props.lineLevelCouponSavings?.savings || 0}
      useCurrencySign
    />
  );

  const {
    endQtyPrice,
    lotQty,
    price: lowestAvailablePrice,
    priceTiers,
    uomLabelVisible: apiUomLabelVisible,
    qtyLabelText: apiQtyLabelText,
    minQty,
  } = navitorPrice || {};

  const navInkPrice = isNavitorProduct && navitorPrice && lowestAvailablePrice;

  const unitPrice = (
    <WBMFormatMoney
      amount={navInkPrice ? lowestAvailablePrice : props.unitPrice}
      useCurrencySign
    />
  );

  const { qtyLabelText, uomLabelVisible } = useMemo(() => {
    if (!isNavitorProduct || !navitorPrice) {
      return { qtyLabelText: "", uomLabelVisible: true };
    }

    const firstTierQty = priceTiers && priceTiers[0]?.qty;
    const navitorNote =
      !endQtyPrice && lotQty && firstTierQty
        ? `For ${firstTierQty}`
        : undefined;

    if (priceTiers?.length === 1 && navitorNote) {
      return { qtyLabelText: navitorNote, uomLabelVisible: false };
    }

    return {
      qtyLabelText: apiQtyLabelText,
      uomLabelVisible: apiUomLabelVisible,
    };
  }, [
    isNavitorProduct,
    navitorPrice,
    endQtyPrice,
    lotQty,
    priceTiers,
    apiQtyLabelText,
    apiUomLabelVisible,
  ]);

  const introduction = useMemo(
    () =>
      endQtyPrice ? PriceIntroduction.AsLowAs : PriceIntroduction.StartingAt,
    [endQtyPrice]
  );

  const displayIndividualUnitData = useMemo(
    () =>
      props.displayUnitPricing &&
      props.individualUnitData &&
      props.individualUnitData.indivUnitCount !== 0 &&
      !props.aboveSalesUnitPrice &&
      !props.belowSalesUnitPrice,
    [
      props.displayUnitPricing,
      props.individualUnitData,
      props.aboveSalesUnitPrice,
      props.belowSalesUnitPrice,
    ]
  );

  return (
    <div
      className={
        props.displayListPrice
          ? "wbm-product-pricing display-list-price"
          : "wbm-product-pricing"
      }
    >
      <div>
        {props.aboveSalesUnitPrice && props.individualUnitData && (
          <div className="small-text">
            <p className="unit-price mb-0"> {unitPriceByIndUnitFormatted}</p>
            <span className="unit-id spacing">
              /{props.individualUnitData.indivUnitLabel}
            </span>
          </div>
        )}
        {props.lineLevelCouponSavings ? (
          <span className="price-row">
            {navitorPrice && <span>{introduction} </span>}
            <span className="original-price">
              {props.salesPrice ? salesPrice : ""}
            </span>
            <span className="discounted-price">
              {props.unitPrice ? unitPrice : ""}
            </span>
            {props.oncePerCustomer && (
              <span className="footnote-indicator">&#8560;</span>
            )}
            {props.qtyLimit > 0 && (
              <span className="footnote-indicator">&#8561;</span>
            )}
            {navitorPrice && !uomLabelVisible ? (
              <span> {qtyLabelText}</span>
            ) : (
              <span className="unit-id">/{props.unitId}</span>
            )}
            <span className="discount">
              {props.lineLevelCouponDisplayType ==
              LineLevelCouponDiscDisplay.Percent ? (
                <>({props.lineLevelCouponSavings.savings}% Savings!)</>
              ) : (
                <>(Save {lineLevelCouponSavings})</>
              )}
            </span>
            {props.belowSalesUnitPrice && props.individualUnitData && (
              <div className="small-text">
                {unitPriceByIndUnitFormatted}
                <span className="unit-id spacing">
                  /{props.individualUnitData.indivUnitLabel}
                </span>
              </div>
            )}
            {introduction === PriceIntroduction.AsLowAs && (
              <div className="min-qty">* Minimum quantity of {minQty}</div>
            )}
          </span>
        ) : (
          <>
            <span className="price-row">
              {navitorPrice && <span>{introduction} </span>}
              <span
                className={
                  props.aboveSalesUnitPrice && props.individualUnitData
                    ? ""
                    : "unit-price"
                }
              >
                {props.unitPrice ? unitPrice : "$0.00"}
              </span>
              {props.oncePerCustomer && (
                <span className="footnote-indicator">&#8560;</span>
              )}
              {props.qtyLimit > 0 && (
                <span className="footnote-indicator">&#8561;</span>
              )}
              {navitorPrice && !uomLabelVisible ? (
                <span> {qtyLabelText}</span>
              ) : (
                <span className="unit-id">/{props.unitId}</span>
              )}
            </span>
            {introduction === PriceIntroduction.AsLowAs && (
              <div className="min-qty">* Minimum quantity of {minQty}</div>
            )}
            {props.belowSalesUnitPrice && props.individualUnitData && (
              <div className="small-text">
                {unitPriceByIndUnitFormatted}
                <span className="unit-id spacing">
                  /{props.individualUnitData.indivUnitLabel}
                </span>
              </div>
            )}
          </>
        )}
      </div>
      <div className={`row no-gutters${!!props.qtyBreaks ? " mt-1" : ""}${props.displayFootnotes && (props.oncePerCustomer || props.qtyLimit > 0) ? " mb-1" : ""}`}>
        {displayIndividualUnitData && (
          <div className="col-auto">
            <div className="small-text">
              {unitPriceByIndUnitFormatted}{" "}
              <span className="unit-id spacing">
                /{props.individualUnitData?.indivUnitLabel}
              </span>
            </div>
          </div>
        )}
        <div className="col-auto">{props.qtyBreaks}</div>
      </div>
      {props.displayFootnotes && (
        <>
          {props.oncePerCustomer && (
            <div className="footnote d-flex">
              &#8560; {props.couponCustomerLimitMessage}
            </div>
          )}
          {props.qtyLimit > 0 && (
            <div className="footnote d-flex">
              &#8561;{" "}
              {props.couponQtyLimitMessage.replace(
                "[LimitQuantity]",
                props.qtyLimit.toString()
              )}
            </div>
          )}
        </>
      )}
      {props.displayListPrice && props.listPrice > 0 && (
        <div className="list-price-container d-flex">
          List: {props.listPrice ? listPrice : "$"}{" "}
          <span className="unit-id">/{props.unitId}</span>
        </div>
      )}
    </div>
  );
};

ProductPrice.defaultProps = {
  couponCustomerLimitMessage: `Discount valid for 1 order per account`,
  couponQtyLimitMessage: `Discount valid on orders of [LimitQuantity] or less.`,
  displayFootnotes: true,
  qtyLimit: 0,
};
